<template>
  <div class="forma-erp kasa-izbornik">

    <div class="traka-ekrana mb-3">
      <div class="naslov">Kasa</div>
      <div class="brojka">
        <span>Firma</span>
        <b>{{ nazivFirme }}</b>
      </div>
      <div class="brojka" v-if="skladiste">
        <span>Skladište</span>
        <b>{{ skladiste }}</b>
      </div>
      <v-spacer></v-spacer>
      <v-progress-circular v-if="ucitavam" indeterminate color="primary" size="20"></v-progress-circular>
    </div>

    <!-- Racun bez JIR-a se ne smije izgubiti iz vida, pa stoji odmah na ulazu
         u kasu, a ne tek na popisu. -->
    <div class="upozorenje mb-3" v-if="cekaju > 0" @click="idi('/crm/kasa/racuni')">
      <v-icon color="orange darken-3">mdi-clock-alert-outline</v-icon>
      <div>
        <b>{{ cekaju }} {{ cekaju === 1 ? 'račun čeka' : 'računa čeka' }} fiskalizaciju.</b>
        <span class="uz">Klik otvara popis.</span>
      </div>
    </div>

    <div class="plocice">
      <div
        v-for="p in plocice"
        :key="p.put || p.radnja"
        class="plocica"
        :class="{probna: p.probna}"
        tabindex="0"
        @click="otvori(p)"
        @keydown.enter.prevent="otvori(p)"
      >
        <v-icon :color="p.probna ? 'grey darken-1' : 'primary'" size="34">{{ p.ikona }}</v-icon>
        <div class="tekst">
          <div class="ime">
            {{ p.naziv }}
            <span class="znak" v-if="p.probna">stara</span>
            <span class="znak cekaju" v-if="p.znak">{{ p.znak }}</span>
          </div>
          <div class="opis">{{ p.opis }}</div>
        </div>
        <v-icon color="grey lighten-1">mdi-chevron-right</v-icon>
      </div>
    </div>

    <!-- Razmjena sa serverom. Kasa radi na svom racunalu, pa racune SALJE ona,
         a sifrarnik s cijenama povlaci - cijene se vode na serveru. -->
    <v-dialog v-model="razmjenaOtvorena" max-width="620">
      <v-card v-if="razmjenaOtvorena">
        <v-card-title class="naslov-razmjena">Server</v-card-title>
        <v-card-text class="pt-4">
          <div class="rz-red">
            <div class="rz-opis">
              <b>Pošalji račune</b>
              <span>Računi ove kase koje server još nije potvrdio. Poslani se ne šalju ponovno.</span>
            </div>
            <div class="rz-gumbi">
              <v-btn small text :loading="radim" @click="posalji(true)">Pregled</v-btn>
              <v-btn small color="primary" :loading="radim" @click="posalji(false)">Pošalji</v-btn>
            </div>
          </div>
          <div class="rz-red">
            <div class="rz-opis">
              <b>Povuci šifrarnik</b>
              <span>Artikli i grupe s cijenama sa servera. Zamjenjuje šifrarnik na kasi u cijelosti.</span>
            </div>
            <div class="rz-gumbi">
              <v-btn small text :loading="radim" @click="osvjezi(true)">Pregled</v-btn>
              <v-btn small color="primary" :loading="radim" @click="osvjezi(false)">Povuci</v-btn>
            </div>
          </div>
          <div v-if="razmjenaPoruka" class="rz-poruka" :class="{lose: razmjenaLose}">{{ razmjenaPoruka }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="razmjenaOtvorena = false">Zatvori</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" bottom :timeout="timeout" :color="color">{{ text }}</v-snackbar>

  </div>
</template>

<script>
import KasaService from '@/services/KasaService'
import RazmjenaPosService from '@/services/RazmjenaPosService'
import dayjs from 'dayjs'

export default {
  name: 'kasaizbornik',

  data () {
    return {
      cekaju: 0,
      ucitavam: false,
      razmjenaOtvorena: false,
      razmjenaPoruka: '',
      razmjenaLose: false,
      radim: false,
      cekajuSlanje: 0,
      snackbar: false,
      text: '',
      color: 'green',
      timeout: 4000
    }
  },

  computed: {
    nazivFirme () {
      return this.$store.state.companyName || ''
    },
    skladiste () {
      return this.$store.state.warehousemarkid || ''
    },
    // Razmjena se nudi samo kad je kasa za nju postavljena (adresa servera i
    // kljuc u postavkama tvrtke) - inace gumb vodi u sigurnu gresku.
    imaRazmjenu () {
      const f = this.$store.state.companyActiv || {}
      return !!(f.RazmjenaUrl && f.RazmjenaKljuc)
    },
    plocice () {
      return [
        {
          naziv: 'Kasa',
          opis: 'Izdavanje računa — skener i tipkovnica',
          ikona: 'mdi-cash-register',
          put: '/crm/kasa'
        },
        {
          naziv: 'Računi',
          opis: 'Pregled izdanih i slanje onih bez JIR-a',
          ikona: 'mdi-receipt-text-outline',
          put: '/crm/kasa/racuni',
          znak: this.cekaju > 0 ? this.cekaju + ' čeka JIR' : ''
        },
        {
          naziv: 'Obračun',
          opis: 'Zbroj po stopama PDV-a i načinima plaćanja',
          ikona: 'mdi-calculator-variant-outline',
          put: '/crm/kasa/obracun'
        },
        ...(this.imaRazmjenu ? [{
          naziv: 'Server',
          opis: 'Pošalji račune i povuci šifrarnik s cijenama',
          ikona: 'mdi-cloud-sync-outline',
          radnja: 'razmjena',
          znak: this.cekajuSlanje > 0 ? this.cekajuSlanje + ' za slanje' : ''
        }] : []),
        {
          // Stara kasa ostaje dostupna dok se nova ne provjeri u radu.
          naziv: 'Kasa test',
          opis: 'Stara kasa (Fiskalizacija 1.0)',
          ikona: 'mdi-flask-outline',
          put: '/crm/document/pos',
          probna: true
        }
      ]
    }
  },

  mounted () {
    this.ucitaj()
    this.prvoPunjenje()
  },

  methods: {
    // Koliko racuna ceka JIR broji posluzitelj - kasa se zna otvoriti i na
    // drugom racunalu, pa se broj ne smije racunati iz ovog ekrana.
    async ucitaj () {
      this.ucitavam = true
      try {
        const {data} = await KasaService.pocetak({
          CompanyId: this.$store.state.companyid,
          BusinessYear: this.$store.state.businessYear,
          SKLADID: this.$store.state.warehousemarkid || 1
        })
        this.cekaju = data.cekaju || 0
      } catch (err) {
        // Izbornik mora raditi i kad posluzitelj ne odgovori - odavde se ide
        // dalje, a svaki ekran sam javi svoj problem.
        this.cekaju = 0
      } finally {
        this.ucitavam = false
      }
    },

    idi (put) {
      this.$router.push(put).catch(() => {})
    },

    otvori (p) {
      if (p.radnja === 'razmjena') {
        this.razmjenaPoruka = ''
        this.razmjenaLose = false
        this.razmjenaOtvorena = true
        return
      }
      this.idi(p.put)
    },

    javi (tekst, boja) {
      this.text = tekst
      this.color = boja || 'green'
      this.timeout = boja === 'red' ? 8000 : 4000
      this.snackbar = true
    },

    tekstGreske (err) {
      const o = err && err.response && err.response.data
      if (o && o.error) { return o.error }
      return err && err.message ? err.message : String(err)
    },

    // Racuni idu od pocetka godine: kasa koja je dan-dva bila bez veze mora
    // poslati i ono od prije, a server sam preskace sto vec ima.
    razdoblje () {
      return {
        odDatuma: dayjs().startOf('year').format('YYYY-MM-DD'),
        doDatuma: dayjs().format('YYYY-MM-DD')
      }
    },

    async posalji (samoPregled) {
      this.radim = true
      this.razmjenaLose = false
      try {
        const {data} = await RazmjenaPosService.posalji(Object.assign({
          CompanyId: this.$store.state.companyid,
          BusinessYear: this.$store.state.businessYear,
          sklad: this.$store.state.warehousemarkid || 0,
          samoPregled: samoPregled === true
        }, this.razdoblje()))

        if (samoPregled) {
          this.cekajuSlanje = data.zaSlanje || 0
          this.razmjenaPoruka = 'Na kasi: ' + data.ukupno + ' računa · za slanje ' + data.zaSlanje +
            ' · već potvrđeno ' + data.preskoceno + '.' + this.poSkladu(data.poSkladu)
          return
        }

        // Zauzet broj nije greska u programu nego posao za knjigovodu: taj broj
        // na serveru vec ima drugi racun.
        const zauzeti = (data.zauzeti || []).map(z => 'skladište ' + z.sklad + ', broj ' + z.broj)
        this.cekajuSlanje = (data.poslano || 0) - (data.potvrdeno || 0)
        this.razmjenaPoruka = 'Poslano ' + data.poslano + ' · potvrdio server ' + data.potvrdeno +
          (zauzeti.length ? ' · zauzet broj: ' + zauzeti.join('; ') : '') +
          (data.greske && data.greske.length ? ' · greške: ' + data.greske.join('; ') : '') + '.' +
          this.poSkladu(data.poSkladu)
        this.razmjenaLose = zauzeti.length > 0 || (data.greske || []).length > 0
        this.javi(data.potvrdeno ? 'Server je potvrdio ' + data.potvrdeno + ' računa.'
          : 'Server nije potvrdio ni jedan račun.', this.razmjenaLose ? 'red' : 'green')
      } catch (err) {
        this.razmjenaPoruka = this.tekstGreske(err)
        this.razmjenaLose = true
      } finally {
        this.radim = false
      }
    },

    poSkladu (po) {
      const kljucevi = Object.keys(po || {})
      if (!kljucevi.length) { return '' }
      return ' Po skladištu: ' + kljucevi.map(k => k + ' → ' + po[k]).join(', ') + '.'
    },

    async osvjezi (samoPregled) {
      this.radim = true
      this.razmjenaLose = false
      try {
        const {data} = await RazmjenaPosService.osvjezi({
          CompanyId: this.$store.state.companyid,
          samoPregled: samoPregled === true
        })
        if (samoPregled) {
          this.razmjenaPoruka = 'Na serveru: ' + data.naServeru.artikli + ' artikala, ' +
            data.naServeru.grupe + ' grupa · na kasi sada: ' + data.naKasi.artikli + ' artikala, ' +
            data.naKasi.grupe + ' grupa.' + (data.samoZaKasu ? ' Server šalje samo artikle označene za kasu.' : '')
          return
        }
        this.razmjenaPoruka = 'Povučeno ' + data.artikli + ' artikala i ' + data.grupe + ' grupa.' +
          (data.bezGrupe ? ' Bez grupe: ' + data.bezGrupe + '.' : '')
        this.javi('Šifrarnik je povučen sa servera.', 'green')
      } catch (err) {
        this.razmjenaPoruka = this.tekstGreske(err)
        this.razmjenaLose = true
      } finally {
        this.radim = false
      }
    },

    // Prazna kasa ne moze izdati racun, a razlog nije ocit. Zato se sifrarnik
    // povlaci sam kad na kasi nema ni jednog artikla - poslije se povlaci na
    // zahtjev, da se ne brise i ne upisuje tisucu artikala pri svakom ulazu.
    async prvoPunjenje () {
      if (!this.imaRazmjenu) { return }
      try {
        const {data} = await RazmjenaPosService.osvjezi({
          CompanyId: this.$store.state.companyid, samoPregled: true
        })
        if (data && data.naKasi && data.naKasi.artikli === 0 && data.naServeru.artikli > 0) {
          await this.osvjezi(false)
        }
      } catch (err) {
        // Izbornik mora raditi i kad server ne odgovori - kasa tada radi s
        // onim sto ima, a razlog se vidi kad se razmjena otvori rukom.
        this.cekajuSlanje = 0
      }
    }
  }
}
</script>

<style scoped>
.kasa-izbornik .upozorenje {
  background: #fff8e1;
  border-left: 4px solid #f9a825;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  border-radius: 4px;
}
.kasa-izbornik .upozorenje b { color: #8d6e00; }
.kasa-izbornik .upozorenje .uz { color: #8d6e00; margin-left: 6px; font-weight: 400; font-size: 13px; }

/* Jedna plocica po retku. Prije ih je "auto-fit" slagao u onoliko stupaca
   koliko stane, pa su na sirokom ekranu stajale sve cetiri jedna uz drugu -
   popis od cetiri izbora citljiviji je odozgo prema dolje nego slijeva
   nadesno. */
.kasa-izbornik .plocice {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  /* Stupac je uzi od ekrana i sredisnji: cetiri izbora razvucena preko cijele
     sirine monitora tjeraju oko s lijevog ruba na desni za svaki redak. */
  max-width: 560px;
  margin: 0 auto;
}
/* Upozorenje o nefiskaliziranim racunima stoji iznad plocica i dio je istog
   stupca, pa prati njihovu sirinu - inace visi preko cijele sirine. */
.kasa-izbornik .upozorenje {
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
}
.kasa-izbornik .plocica {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #dbe2e8;
  border-radius: 6px;
  cursor: pointer;
}
.kasa-izbornik .plocica:hover,
.kasa-izbornik .plocica:focus {
  border-color: #1976d2;
  box-shadow: 0 2px 10px rgba(25, 118, 210, 0.14);
  outline: none;
}
/* Stara kasa je blijeda: tu se ide samo dok nova ne prode provjeru. */
.kasa-izbornik .plocica.probna { background: #fafbfc; }
.kasa-izbornik .plocica .tekst { flex: 1 1 auto; min-width: 0; }
.kasa-izbornik .plocica .ime {
  font-size: 17px;
  font-weight: 700;
  color: #1565c0;
}
.kasa-izbornik .plocica.probna .ime { color: #546e7a; }
.kasa-izbornik .plocica .opis { font-size: 13px; color: #78909c; margin-top: 2px; }
.kasa-izbornik .plocica .znak {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 1px 6px;
  border-radius: 3px;
  margin-left: 8px;
  background: #eceff1;
  color: #607d8b;
  vertical-align: middle;
}
.kasa-izbornik .plocica .znak.cekaju { background: #fff3e0; color: #e65100; }
.naslov-razmjena {
  background: #1565c0;
  color: #fff;
  font-size: 17px;
  padding: 10px 16px;
}
.rz-red {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eceff1;
}
.rz-red:last-of-type { border-bottom: 0; }
.rz-opis { flex: 1 1 auto; min-width: 0; }
.rz-opis b { display: block; color: #1565c0; }
.rz-opis span { font-size: 13px; color: #78909c; }
.rz-gumbi { flex: 0 0 auto; white-space: nowrap; }
.rz-poruka {
  margin-top: 10px;
  padding: 8px 10px;
  background: #f5f7f9;
  border-left: 3px solid #1976d2;
  font-size: 13px;
}
.rz-poruka.lose { background: #fff8e1; border-left-color: #f9a825; color: #8d6e00; }
</style>
